import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

interface TabItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

interface TabBarProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (id: string) => void;
  className?: string;
}

export function TabBar({ tabs, activeTab, onChange, className }: TabBarProps) {
  const { t } = useT();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const update = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setOverflow({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  };

  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [tabs.length]);

  useEffect(() => {
    // jsdom n'implémente pas scrollIntoView : appel défensif.
    activeRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    update();
  }, [activeTab]);

  if (tabs.length <= 1) return null;

  const nudge = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    el?.scrollBy?.({ left: dir * Math.max(el.clientWidth * 0.6, 120), behavior: 'smooth' });
  };

  return (
    <div className={cn('relative rounded-xl border border-slate-200 bg-white dark:border-ink-700 dark:bg-ink-800', className)}>
      <div ref={scrollerRef} onScroll={update} className="tab-scroll overflow-x-auto py-1.5">
        {/* px-2 sur la piste, pas sur le conteneur défilant : le padding d'un
            élément scrollable ne s'applique pas en fin de défilement. */}
        <div className="flex gap-1 px-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              ref={activeTab === tab.id ? activeRef : undefined}
              type="button"
              onClick={() => onChange(tab.id)}
              disabled={tab.disabled}
              aria-current={activeTab === tab.id ? 'true' : undefined}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition',
                activeTab === tab.id
                  ? 'bg-brand-500 text-white shadow'
                  : 'text-ink-500 hover:bg-slate-100 dark:hover:bg-ink-700',
                tab.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {tab.icon && <tab.icon className="h-3.5 w-3.5" />}
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {overflow.left && (
        <button
          type="button"
          onClick={() => nudge(-1)}
          aria-label={t('tabs.scroll.left')}
          className="absolute inset-y-0 left-0 flex w-8 items-center justify-start rounded-l-xl bg-gradient-to-r from-white via-white/80 to-transparent pl-1 text-ink-500 dark:from-ink-800 dark:via-ink-800/80"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {overflow.right && (
        <button
          type="button"
          onClick={() => nudge(1)}
          aria-label={t('tabs.scroll.right')}
          className="absolute inset-y-0 right-0 flex w-8 items-center justify-end rounded-r-xl bg-gradient-to-l from-white via-white/80 to-transparent pr-1 text-ink-500 dark:from-ink-800 dark:via-ink-800/80"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
